import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  belegExternalNumberQueryKey,
  fetchBelegExternalNumber,
  saveBelegExternalNumber,
} from "../api/belege";
import { ApiError } from "../lib/api";
import { formatDateShort } from "../lib/format";
import PencilIcon from "./PencilIcon";

/** Own accounting number (ER-Nummer) of a Rechnung or Gutschrift (#529). */
export default function BelegExternalNumber({ belegId }: { belegId: string }) {
  const queryClient = useQueryClient();
  const queryKey = belegExternalNumberQueryKey(belegId);
  // A missing field must never break the Beleg page around it.
  const query = useQuery({ queryKey, queryFn: () => fetchBelegExternalNumber(belegId), retry: false });
  const [draft, setDraft] = useState<string | null>(null);
  const requestId = useRef<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const editing = draft !== null;

  useEffect(() => {
    if (editing) inputRef.current?.focus();
  }, [editing]);

  const mutation = useMutation({
    mutationFn: (value: string) =>
      saveBelegExternalNumber({
        belegId,
        requestId: (requestId.current ??= crypto.randomUUID()),
        expectedRevision: query.data?.revision ?? 0,
        externalNumber: value.trim() || null,
      }),
    onSuccess: (data) => {
      queryClient.setQueryData(queryKey, data);
      void queryClient.invalidateQueries({ queryKey: ["belege"], exact: false, predicate: (q) => q.queryKey[1] !== "external-number" });
      void queryClient.invalidateQueries({ queryKey: ["article"], predicate: (q) => q.queryKey[2] === "price-history" || q.queryKey[2] === "credits" });
      void queryClient.invalidateQueries({ queryKey: ["cost-center-period-spend"] });
      requestId.current = null;
      setDraft(null);
    },
    onError: (error) => {
      if (error instanceof ApiError && error.status === 409) {
        requestId.current = null;
        void query.refetch();
      }
    },
  });

  if (query.isLoading) return <p className="mt-2 text-sm text-slate-500">Lade ER-Nummer …</p>;
  if (query.isError || !query.data) return null;
  const data = query.data;
  const latest = data.history[0];

  function startEditing() {
    requestId.current = null;
    mutation.reset();
    setDraft(data.external_number ?? "");
  }

  if (editing) {
    return (
      <form
        className="mt-2 flex flex-wrap items-center gap-2 text-sm"
        onSubmit={(event) => {
          event.preventDefault();
          mutation.mutate(draft);
        }}
      >
        <label htmlFor="beleg-external-number" className="font-medium text-slate-700">
          ER-Nummer
        </label>
        <input
          id="beleg-external-number"
          ref={inputRef}
          value={draft}
          maxLength={64}
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Escape") setDraft(null);
          }}
          className="h-9 w-56 rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-900 focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
        />
        <button
          type="submit"
          disabled={mutation.isPending}
          className="h-9 rounded-md bg-emerald-700 px-3 text-sm font-medium text-white hover:bg-emerald-800 disabled:opacity-50"
        >
          {draft.trim() || !data.external_number ? "Speichern" : "Entfernen"}
        </button>
        <button
          type="button"
          onClick={() => setDraft(null)}
          className="h-9 rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-700 hover:bg-slate-50"
        >
          Abbrechen
        </button>
        {mutation.isError ? (
          <p role="alert" className="w-full text-sm text-rose-700">
            {mutation.error instanceof ApiError ? mutation.error.message : "Die ER-Nummer konnte nicht gespeichert werden."}
          </p>
        ) : null}
      </form>
    );
  }

  return (
    <div className="mt-3 text-sm">
      <p className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <span className="text-slate-600">ER-Nummer</span>
        {data.external_number ? (
          <span className="text-base font-semibold text-slate-900">{data.external_number}</span>
        ) : data.can_edit ? null : (
          <span className="text-slate-500">nicht zugeordnet</span>
        )}
        {data.can_edit && !data.external_number ? (
          <button
            type="button"
            onClick={startEditing}
            className="inline-flex items-center gap-1 text-sm font-medium text-emerald-700 hover:underline focus-visible:rounded-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-emerald-700"
            aria-label="ER-Nummer zuordnen"
          >
            <PencilIcon />
            Zuordnen
          </button>
        ) : null}
        {data.can_edit && data.external_number ? (
          <button
            type="button"
            onClick={startEditing}
            className="inline-flex h-8 items-center gap-1.5 rounded-md border border-slate-300 bg-white px-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50"
          >
            <PencilIcon />
            Bearbeiten
          </button>
        ) : null}
      </p>
      {latest ? (
        <p className="mt-1 text-xs text-slate-500">
          {latest.external_number ? "zuletzt geändert" : "entfernt"} von {latest.actor} am {formatDateShort(latest.created_at)}
        </p>
      ) : null}
      {data.duplicates.length > 0 ? (
        <p className="mt-1 text-xs text-amber-800">
          Dieselbe ER-Nummer steht auch an{" "}
          {data.duplicates.map((duplicate, index) => (
            <span key={duplicate.beleg_id}>
              {index > 0 ? ", " : ""}
              <Link className="underline" to={`/belege/${encodeURIComponent(duplicate.beleg_id)}`}>
                {duplicate.doc_type === "gutschrift" ? "Gutschrift" : "Rechnung"} {duplicate.doc_number ?? duplicate.beleg_id}
              </Link>
            </span>
          ))}
          .
        </p>
      ) : null}
    </div>
  );
}
